import React, { useEffect, useState, useCallback, useMemo, useContext, useRef } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField as MuiTextField,
  Alert as MuiAlert,
  Typography,
  Snackbar,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import SelectWrapper from "./SelectBox";
import moment from "moment";
import apiUtils from "../utils/apiUtils";
import { useDropzone } from "react-dropzone";
import { GlobalContext } from "../contexts/GlobalContext";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITIES } from "../auth/permissions";
import CreateSupplierDialog from "./manualInvoices/CreateSupplierDialog";
import CustomerSearchSelect from "./CustomerSearchSelect";
import CustomerClipboardOverlay from "./manualInvoices/CustomerClipboardOverlay";
import SupplierProductNotesOverlay from "./manualInvoices/SupplierProductNotesOverlay";
import {
  buildReviewOcrInvoiceFieldDefinitions,
  FocusFirstEmptyInvoiceFieldEffect,
} from "../utils/focusFirstEmptyInvoiceField";
// import { set } from "date-fns";

const Card = styled(MuiCard)(spacing);
const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

const FORM_FIELD_WIDTH = 215;

// Helper to format numbers to two decimal places
const formatToTwoDecimals = (value) => {
  if (value === null || value === undefined || value === "") {
    return ""; // Return empty string for empty/null/undefined
  }
  // Replace comma with dot for European number format support
  const normalizedValue = String(value).replace(",", ".");
  const number = parseFloat(normalizedValue);
  if (isNaN(number)) {
    return ""; // Return empty string if not a valid number
  }
  return number.toFixed(2);
};

const parseNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const getInvoiceDateHelperText = (invoiceDate) => {
  if (!invoiceDate) {
    return "";
  }

  const parsedInvoiceDate = moment(invoiceDate).startOf("day");
  if (!parsedInvoiceDate.isValid()) {
    return "";
  }

  const today = moment().startOf("day");
  if (parsedInvoiceDate.isAfter(today)) {
    return "Invoice date is in the future";
  }

  const quarterDeadline = parsedInvoiceDate.endOf("quarter").add(45, "days").startOf("day");
  if (today.isAfter(quarterDeadline)) {
    return "Invoice date falls outside claim period";
  }

  return "";
};

/** Sort product type/subcode dropdown options by key (numeric-aware); "98" (N/A) last. */
const sortProductTypeDropdownOptions = (items) =>
  [...items].sort((a, b) => {
    const ka = String(a.key);
    const kb = String(b.key);
    const aIs98 = ka === "98";
    const bIs98 = kb === "98";
    if (aIs98 !== bIs98) {
      return aIs98 ? 1 : -1;
    }
    return ka.localeCompare(kb, undefined, { numeric: true, sensitivity: "base" });
  });

// Validation schema factory
const createValidationSchema = (showExtraFields) => {
  let schema = Yup.object().shape({
    customerNumber: Yup.string().required("Required"),
    invoiceNumber: Yup.string().required("Required"),
    invoiceDate: Yup.string().required("Required"),
    supplierId: Yup.string().required("Required"),
    countryCode: Yup.string().required("Required"),
    claimProductTypeCode: Yup.string().required("Required"),
    claimProductSubCodeId: Yup.string().required("Required"),
    currencyId: Yup.string().required("Required"),
    invoiceGrossAmount: Yup.string()
      .required("Required")
      .matches(/^-?[0-9]+([.,][0-9]+)?$/, "Invalid format. Only numbers, dots or commas, and an optional negative sign are allowed."),
    invoiceVATAmountLocalCurrency: Yup.string()
      .required("Required")
      .matches(/^-?[0-9]+([.,][0-9]+)?$/, "Invalid format. Only numbers, dots or commas, and an optional negative sign are allowed."),
  });

  if (showExtraFields) {
    schema = schema.concat(
      Yup.object().shape({
        languageCode: Yup.string().required("Language is required"),
        freeText: Yup.string().required("Free text is required"),
      })
    );
  }

  return schema;
};

export default function ViewCheckAutomatedInvoiceDialog(props) {
  const { viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog, activeTab, updateTimestamp } = props;
  const { globalData } = useContext(GlobalContext);
  const { hasPermission } = usePermissions();
  const canWriteInvoices = hasPermission(CAPABILITIES.invoicesWrite, "W");

  // console.log("ViewCheckAutomatedInvoiceDialog -> viewCustomerInvoiceDialog", viewCustomerInvoiceDialog);

  const [openAlert, setOpenAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  // console.log("ViewCheckAutomatedInvoiceDialog -> viewCustomerInvoiceDialog", props);

  const [showExtraFields, setShowExtraFields] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [isCreateSupplierDialogOpen, setIsCreateSupplierDialogOpen] = useState(false);
  const [createdSuppliers, setCreatedSuppliers] = useState([]);
  const [invoiceNumberInfo, setInvoiceNumberInfo] = useState("");
  const formHelpersRef = useRef(null);
  const invoiceFormPanelRef = useRef(null);
  const invoiceNumberCheckTimeoutRef = useRef(null);

  // File upload state
  const [isNewFile, setIsNewFile] = useState(false);
  const [isUpdatedFile, setIsUpdatedFile] = useState(false);
  const [isRemovedFile, setIsRemovedFile] = useState(false);
  const [fileContent, setFileContent] = useState(null);

  const productTypeData = apiUtils.useApiData("/api/v1/productTypes");
  const customerInvoicesData = apiUtils.useApiData("/api/v1/customerInvoices");

  const mergedSupplierData = useMemo(() => {
    if (!createdSuppliers.length) {
      return props.supplierArr || [];
    }

    const suppliersById = new Map();
    [...(props.supplierArr || []), ...createdSuppliers].forEach((supplier) => {
      if (!supplier?.supplierId) {
        return;
      }

      suppliersById.set(supplier.supplierId, supplier);
    });

    return Array.from(suppliersById.values());
  }, [createdSuppliers, props.supplierArr]);

  const initialCustomProductLanguage = viewCustomerInvoiceDialog?.params?.productCodeDescriptionLanguageCountry;
  const initialProductCodeDescription = viewCustomerInvoiceDialog?.params?.productCodeDescription;

  const initialProductTypeId = parseInt(viewCustomerInvoiceDialog?.params?.productId, 10);

  const [currentCountryCode, setCurrentCountryCode] = useState("");
  const [currentProductCode, setCurrentProductCode] = useState("");
  const [currentProductSubCode, setCurrentProductSubCode] = useState("");

  const [groupedProductCodes, setGroupedProductCodes] = useState([]);
  const [groupedProductSubCodes, setGroupedProductSubCodes] = useState([]);

  const extractVATNumber = (taxId, countryArr) => {
    if (!taxId) return "";
    const sortedCountries = [...countryArr].sort((a, b) => b.countryCode.length - a.countryCode.length);
    const matchedCountry = sortedCountries.find((country) => taxId.toUpperCase().startsWith(country.countryCode.toUpperCase()));
    return matchedCountry ? taxId.slice(matchedCountry.countryCode.length) : taxId;
  };

  const [currentSupplierId, setCurrentSupplierId] = useState(null);

  // Function to find the initial supplier ID based on taxId, suppliers, and countries
  const findInitialSupplierId = useCallback(
    (taxIdSupplier, suppliers, countries) => {
      if (!taxIdSupplier || !suppliers || !countries) return ""; // Return empty string if essential data is missing

      const vatNumber = extractVATNumber(taxIdSupplier, countries);
      const supplier = suppliers.find((s) => s.supplierVATnr === vatNumber);
      return supplier ? supplier.supplierId : ""; // Return ID or empty string
    },
    [extractVATNumber]
  ); // Assuming extractVATNumber is stable or memoized if complex

  // Calculate the initial supplier ID before defining initialValues
  const initialSupplierId = useMemo(() => {
    return findInitialSupplierId(viewCustomerInvoiceDialog?.params?.taxIdSupplier, mergedSupplierData, props.countryArr);
  }, [viewCustomerInvoiceDialog?.params?.taxIdSupplier, mergedSupplierData, props.countryArr, findInitialSupplierId]);

  useEffect(() => {
    setCreatedSuppliers([]);
  }, [viewCustomerInvoiceDialog?.params?.id]);

  useEffect(() => {
    setInvoiceNumberInfo("");
  }, [viewCustomerInvoiceDialog?.params?.id]);

  const isDuplicateInvoiceForCustomer = useCallback(
    (customerNumber, invoiceNumber) => {
      const trimmedCustomerNumber = String(customerNumber || "").trim();
      const trimmedInvoiceNumber = String(invoiceNumber || "").trim().toLowerCase();

      if (!trimmedCustomerNumber || !trimmedInvoiceNumber || !customerInvoicesData?.length) {
        return false;
      }

      const customer = props.entitiesArr?.find((entity) => entity.entityNumber === trimmedCustomerNumber);
      if (!customer?.entityId) {
        return false;
      }

      const excludeCustomerInvoiceId = viewCustomerInvoiceDialog?.params?.customerInvoiceId;

      return customerInvoicesData.some(
        (invoice) =>
          Number(invoice.entityId) === Number(customer.entityId) &&
          String(invoice.invoiceNumber || "").trim().toLowerCase() === trimmedInvoiceNumber &&
          (!excludeCustomerInvoiceId || Number(invoice.customerInvoiceId) !== Number(excludeCustomerInvoiceId))
      );
    },
    [customerInvoicesData, props.entitiesArr, viewCustomerInvoiceDialog?.params?.customerInvoiceId]
  );

  const checkInvoiceNumberDuplicate = useCallback(() => {
    const values = formHelpersRef.current?.values;
    if (!values) {
      setInvoiceNumberInfo("");
      return;
    }

    setInvoiceNumberInfo(
      isDuplicateInvoiceForCustomer(values.customerNumber, values.invoiceNumber)
        ? "Info: invoice number already exists for this customer."
        : ""
    );
  }, [isDuplicateInvoiceForCustomer]);

  const scheduleInvoiceNumberCheck = useCallback(() => {
    if (invoiceNumberCheckTimeoutRef.current) {
      clearTimeout(invoiceNumberCheckTimeoutRef.current);
    }

    invoiceNumberCheckTimeoutRef.current = setTimeout(() => {
      checkInvoiceNumberDuplicate();
    }, 250);
  }, [checkInvoiceNumberDuplicate]);

  const handleInvoiceNumberBlur = useCallback(() => {
    checkInvoiceNumberDuplicate();
  }, [checkInvoiceNumberDuplicate]);

  const handleClipboardNotify = useCallback((severity, message) => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setOpenAlert(true);
  }, []);

  const getClipboardFormValues = useCallback(() => {
    const values = formHelpersRef.current?.values;
    return {
      countryCode: values?.countryCode || "",
      supplierId: values?.supplierId || "",
      claimProductTypeCode: values?.claimProductTypeCode || "",
      claimProductSubCode: values?.claimProductSubCodeId || "",
    };
  }, []);

  const handleClipboardApply = useCallback(
    (applied) => {
      const helpers = formHelpersRef.current;
      if (!helpers) {
        return;
      }

      const { setFieldValue } = helpers;
      setFieldValue("countryCode", applied.countryCode);
      setFieldValue("supplierId", applied.supplierId);
      setFieldValue("claimProductTypeCode", applied.claimProductTypeCode);
      setFieldValue("claimProductSubCodeId", applied.claimProductSubCode);
      if (applied.currencyId) {
        setFieldValue("currencyId", applied.currencyId);
      }

      setCurrentCountryCode(applied.countryCode || "");
      setCurrentProductCode(applied.claimProductTypeCode || "");
      setCurrentProductSubCode(applied.claimProductSubCode || "");
      scheduleInvoiceNumberCheck();
    },
    [scheduleInvoiceNumberCheck]
  );

  useEffect(() => {
    if (!viewCustomerInvoiceDialog?.params?.id) {
      return undefined;
    }

    const debounceId = setTimeout(() => {
      checkInvoiceNumberDuplicate();
    }, 250);

    return () => clearTimeout(debounceId);
  }, [
    checkInvoiceNumberDuplicate,
    viewCustomerInvoiceDialog?.params?.id,
    viewCustomerInvoiceDialog?.params?.customerNumber,
    viewCustomerInvoiceDialog?.params?.invoiceNumber,
    customerInvoicesData,
  ]);

  useEffect(() => {
    return () => {
      if (invoiceNumberCheckTimeoutRef.current) {
        clearTimeout(invoiceNumberCheckTimeoutRef.current);
      }
    };
  }, []);

  // Calculate the initial country ID synchronously
  const initialCountryId = useMemo(() => {
    const countryCodeSupplier = viewCustomerInvoiceDialog?.params?.countryCodeSupplier;
    if (!countryCodeSupplier || !props.countryArr) return "";
    const country = props.countryArr.find((c) => c.countryCode === countryCodeSupplier);
    return country ? country.countryId : "";
  }, [viewCustomerInvoiceDialog?.params?.countryCodeSupplier, props.countryArr]);

  // Calculate the initial product code and sub-code synchronously
  const { initialProductCode, initialProductSubCode } = useMemo(() => {
    if (!productTypeData || !initialProductTypeId) {
      return { initialProductCode: "", initialProductSubCode: "" };
    }
    const product = productTypeData.find((p) => p.productTypeId === initialProductTypeId);
    if (product) {
      return {
        initialProductCode: product.categoryCode || "",
        initialProductSubCode: product.subCode === "" ? "98" : product.subCode,
      };
    }
    return { initialProductCode: "", initialProductSubCode: "" };
  }, [productTypeData, initialProductTypeId]);

  // Update current states based on calculated initial values
  useEffect(() => {
    setCurrentSupplierId(initialSupplierId);
  }, [initialSupplierId]);

  useEffect(() => {
    setCurrentCountryCode(initialCountryId);
  }, [initialCountryId]);

  useEffect(() => {
    setCurrentProductCode(initialProductCode);
  }, [initialProductCode]);

  useEffect(() => {
    setCurrentProductSubCode(initialProductSubCode);
  }, [initialProductSubCode]);

  useEffect(() => {
    if (updateTimestamp) {
      // Set alert state
      setAlertSeverity("success"); // You can change this based on the update result
      setAlertMessage("invoice has been successfully updated!");
      setOpenAlert(true);
    }
  }, [updateTimestamp]);

  useEffect(() => {
    if (viewCustomerInvoiceDialog?.params?.countryCodeSupplier) {
      const country = props.countryArr.find((country) => country.countryCode === viewCustomerInvoiceDialog.params.countryCodeSupplier);
      if (country) {
        setCurrentCountryCode(country.countryId);
      } else {
        setCurrentCountryCode("");
      }
    } else {
      setCurrentCountryCode("");
    }
  }, [viewCustomerInvoiceDialog?.params?.countryCodeSupplier, props.countryArr]);

  useEffect(() => {
    if (productTypeData && productTypeData.length > 0) {
      console.log("productTypeData", props.countryArr);
      const productCodes = productTypeData
        .filter((product) => product.countryCode === currentCountryCode)
        .map((product) => ({
          key: product.categoryCode,
          value: product.productShortDescription,
        }))
        .reduce((accumulator, current) => {
          // Check if the key already exists in the accumulator
          if (!accumulator.some((item) => item.key === current.key)) {
            accumulator.push(current);
          }
          return accumulator;
        }, []);

      console.log(productCodes);
      setGroupedProductCodes(sortProductTypeDropdownOptions(productCodes));
      let productSubCodes = productTypeData
        .filter((product) => product.countryCode === currentCountryCode && product.categoryCode === currentProductCode)
        .map((product) => ({
          key: product.subCode,
          value: product.productLongDescription,
        }));

      // Check if there's an entry with empty SubCode
      const hasEmptySubCode = productSubCodes.some((item) => item.key === "");

      if (productSubCodes.length === 0 || (productSubCodes[0].key === "" && productSubCodes.length === 1)) {
        // No subcodes at all, OR exactly one empty subcode - use only "98 N/A"
        productSubCodes = [{ key: "98", value: "98. N/A" }];
      } else if (hasEmptySubCode) {
        // There's an empty SubCode entry AND other subcodes exist - add "98 N/A" if not already present
        const has98 = productSubCodes.some((item) => item.key === "98");
        if (!has98) {
          productSubCodes.push({ key: "98", value: "98. N/A" });
        }
        // Remove the empty SubCode entry since it's represented by "98"
        productSubCodes = productSubCodes.filter((item) => item.key !== "");
      }
      console.log(productSubCodes);
      setGroupedProductSubCodes(sortProductTypeDropdownOptions(productSubCodes));
    }
  }, [currentProductCode, currentCountryCode, productTypeData, props.countryArr]);

  const [fileUrl, setFileUrl] = useState(null);
  const downloadFile = useCallback(
    async (fileId, modelUsed, hardError, isFromAttachment = false) => {
      setFileUrl(""); // Reset file URL initially
      if (fileId !== "" && fileId != null) {
        try {
          let url;
          if (isFromAttachment) {
            // Download from regular blob storage (added via dropzone)
            url = `/api/v1/azureblobstorage/${fileId}`;
          } else {
            // Download from OCR blob storage (original file)
            url = `/api/v1/azureocrblobstorage/${fileId}/${modelUsed}/${hardError}`;
          }

          console.log("Downloading file from:", url);
          const response = await axios({
            url: url,
            method: "GET",
            responseType: "blob",
          });
          const href = URL.createObjectURL(response.data);
          console.log("href", href);
          setFileUrl(href);
        } catch (error) {
          setFileUrl("NoFile");
          console.error("Error downloading file:", error);
        }
      } else {
        setFileUrl("NoFile");
      }
    },
    [activeTab]
  );

  useEffect(() => {
    if (viewCustomerInvoiceDialog?.downloadFile) {
      // First check if there's a fileId from an added attachment (regular blob storage)
      const fileIdFromAttachment = viewCustomerInvoiceDialog?.params?.fileId;
      // Fallback to original file from OCR blob storage
      const newFileName = viewCustomerInvoiceDialog?.params?.newFileName;
      const modelUsed = viewCustomerInvoiceDialog?.params?.modelUsed;
      let hardError = viewCustomerInvoiceDialog?.params?.prevHardError ?? 0;

      if (activeTab === 3 || viewCustomerInvoiceDialog?.params?.prevHardError === 1) {
        hardError = 1;
      }

      // Prioritize fileId from attachment (regular blob storage)
      if (fileIdFromAttachment && fileIdFromAttachment > 0) {
        downloadFile(fileIdFromAttachment, modelUsed, hardError, true);
      } else if (newFileName && newFileName !== "" && newFileName !== null) {
        // Fallback to original file from OCR blob storage
        downloadFile(newFileName, modelUsed, hardError, false);
      } else {
        setFileUrl("NoFile");
      }

      setViewCustomerInvoiceDialog((prevState) => ({
        ...prevState,
        downloadFile: false,
      }));
    }
  }, [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog, downloadFile, activeTab]);

  // Show extra fields based on Product Code Description
  useEffect(() => {
    const productCodeDescription = viewCustomerInvoiceDialog?.params?.productCodeDescription;
    const productCodeDescriptionLanguageCountry = viewCustomerInvoiceDialog?.params?.productCodeDescriptionLanguageCountry;

    if (productCodeDescription && productCodeDescriptionLanguageCountry) {
      setShowExtraFields(true);
    } else {
      setShowExtraFields(false);
    }
  }, [viewCustomerInvoiceDialog]);

  const customSelectableLanguages = [
    { key: "en", value: "English" },
    { key: "ro", value: "Romanian" },
    { key: "cz", value: "Czech" },
    { key: "sk", value: "Slovak" },
  ];

  // File drop handler
  const onDropAccepted = useCallback(
    (acceptedFiles) => {
      const selectedFile = acceptedFiles[0];
      const href = URL.createObjectURL(selectedFile);
      setFileUrl(href);
      setFileContent(selectedFile);
      if (isRemovedFile) {
        setIsUpdatedFile(true);
      } else {
        setIsNewFile(true);
      }
      setIsRemovedFile(false);
    },
    [isRemovedFile]
  );

  // Dropzone styles
  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
    cursor: "pointer",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    boxSizing: "border-box",
  };

  const focusedStyle = {
    borderColor: "#2196f3",
  };

  const acceptStyle = {
    borderColor: "#00e676",
  };

  const rejectStyle = {
    borderColor: "#ff1744",
  };

  const StyledDropzone = () => {
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
      accept: { "application/pdf": [] },
      onDropAccepted,
    });

    const style = useMemo(
      () => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
      }),
      [isFocused, isDragAccept, isDragReject]
    );

    return (
      <div className="container">
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <p
            style={{
              display: "flex",
              margin: "auto",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            No files are currently attached to this invoice. <br />
            Drag 'n' drop the invoice file here, or click to select
          </p>
        </div>
      </div>
    );
  };

  const handleConfirmationDialogClose = () => {
    setShowConfirmationDialog(false);
  };

  const handleSelectedProductCatagoryChange = (event, setFieldValue) => {
    const newProductCode = event.target.value;
    setCurrentProductCode(newProductCode);
    // Reset the sub code when product type changes
    setFieldValue("claimProductSubCodeId", "");
  };

  const handleCloseCreateSupplierDialog = useCallback(() => {
    setIsCreateSupplierDialogOpen(false);
  }, []);

  const handleSupplierCreated = useCallback(
    (newSupplier) => {
      setCreatedSuppliers((prevSuppliers) => {
        const existingIndex = prevSuppliers.findIndex((supplier) => supplier.supplierId === newSupplier.supplierId);
        if (existingIndex === -1) {
          return [...prevSuppliers, newSupplier];
        }

        return prevSuppliers.map((supplier, index) => (index === existingIndex ? newSupplier : supplier));
      });

      setIsCreateSupplierDialogOpen(false);

      if (!newSupplier?.supplierId) {
        return;
      }

      const helpers = formHelpersRef.current;
      if (!helpers) {
        return;
      }

      const { setFieldValue, values } = helpers;
      const nextCountryCode = newSupplier.countryCode || values.countryCode;
      const countryChanged = Boolean(nextCountryCode && nextCountryCode !== values.countryCode);
      const matchingCountry = props.countryArr.find((item) => item.countryId === nextCountryCode);

      if (nextCountryCode && nextCountryCode !== values.countryCode) {
        setCurrentCountryCode(nextCountryCode);
        setFieldValue("countryCode", nextCountryCode);
      }

      setCurrentSupplierId(newSupplier.supplierId);
      setFieldValue("supplierId", newSupplier.supplierId);
      setFieldValue("taxIdSupplier", newSupplier.supplierVATnr || "");

      if (countryChanged) {
        setCurrentProductCode("");
        setCurrentProductSubCode("");
        setFieldValue("claimProductTypeCode", "");
        setFieldValue("claimProductSubCodeId", "");

        if (matchingCountry?.currency) {
          setFieldValue("currencyId", matchingCountry.currency);
        }
      }
    },
    [props.countryArr]
  );

  const handleSupplierChange = (event, setFieldValue, handleChange, currentCountryCode) => {
    const selectedSupplierId = event.target.value;
    setCurrentSupplierId(selectedSupplierId); // Update state

    const selectedSupplier = mergedSupplierData.find((supplier) => supplier.supplierId === selectedSupplierId);
    if (selectedSupplier) {
      // Check if the supplier's country exists and is different from the current country
      if (selectedSupplier.countryCode && selectedSupplier.countryCode !== currentCountryCode) {
        setCurrentCountryCode(selectedSupplier.countryCode);
        setFieldValue("countryCode", selectedSupplier.countryCode);
        // Reset product fields because country changed
        setCurrentProductCode("");
        setCurrentProductSubCode("");
        setFieldValue("claimProductTypeCode", "");
        setFieldValue("claimProductSubCodeId", "");
      }
      // Always update Tax ID if supplier is found
      setFieldValue("taxIdSupplier", selectedSupplier.supplierVATnr || "");
    }
    // Let Formik handle the change to the supplierId field itself
    handleChange(event);
  };

  // Initial form values
  const initialValues =
    activeTab === 3
      ? {
          customerId: "",
          invoiceNumber: "",
          customerNumber: "",
          invoiceDate: "",
          supplierId: "",
          countryCode: "",
          invoiceStateIdStatic: 3,
          productTypeId: "",
          claimProductTypeCode: "",
          currencyId: "",
          invoiceGrossAmount: "",
          invoiceVATAmountLocalCurrency: "",
          invoiceVATAmountEUR: "",
          claimId: "",
          invoiceLabelId: "",
          languageCode: "",
          freeText: "",
          claimProductSubCodeId: "",
        }
      : {
          customerId: viewCustomerInvoiceDialog?.params?.entityId,
          invoiceNumber: viewCustomerInvoiceDialog?.params?.invoiceNumber || "",
          customerNumber: viewCustomerInvoiceDialog?.params?.customerNumber || "",
          invoiceDate: viewCustomerInvoiceDialog?.params?.invoiceDate
            ? moment(viewCustomerInvoiceDialog.params.invoiceDate).format("YYYY-MM-DD")
            : "",
          supplierId: initialSupplierId || "",
          countryCode: initialCountryId || "",
          invoiceStateIdStatic: viewCustomerInvoiceDialog?.params?.invoiceStateId ?? 3,
          productTypeId: viewCustomerInvoiceDialog?.params?.productTypeId || "",
          claimProductTypeCode: initialProductCode || "",
          currencyId: viewCustomerInvoiceDialog?.params?.currencyId || "",
          invoiceGrossAmount: formatToTwoDecimals(viewCustomerInvoiceDialog?.params?.totalExcludingTax),
          invoiceVATAmountLocalCurrency: formatToTwoDecimals(viewCustomerInvoiceDialog?.params?.totalTax),
          invoiceVATAmountEUR: viewCustomerInvoiceDialog?.params?.invoiceVATAmountEUR || "",
          claimId: viewCustomerInvoiceDialog?.params?.claimId || "",
          invoiceLabelId: viewCustomerInvoiceDialog?.params?.invoiceLabelId || "",
          languageCode: initialCustomProductLanguage || "",
          freeText: viewCustomerInvoiceDialog?.params?.freeText
            ? viewCustomerInvoiceDialog?.params?.freeText
            : initialProductCodeDescription || "",
          claimProductSubCodeId: initialProductSubCode || "",
        };

  const validationSchema = createValidationSchema(showExtraFields);

  // Form submission handler
  const handleSubmit = async (values, { setErrors }) => {
    if (!canWriteInvoices) {
      return;
    }

    const matchedProduct = productTypeData.find(
      (item) =>
        item.categoryCode === values.claimProductTypeCode &&
        item.countryCode === values.countryCode &&
        item.subCode === (values.claimProductSubCodeId === "98" ? "" : values.claimProductSubCodeId)
    );

    let newCustomer = props.entitiesArr.find((entity) => entity.entityNumber === values.customerNumber);
    if (!newCustomer) {
      return setErrors({ customerNumber: "Customer not found" });
    }

    console.log("viewCustomerInvoiceDialog", viewCustomerInvoiceDialog);

    // Handle file upload if there's a new file
    let uploadedFileId = null; // Store the uploaded fileId
    if (isNewFile || isUpdatedFile) {
      try {
        if (!isRemovedFile && fileContent) {
          const fileInfo = {
            documentType: 1,
            creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
            createdBy: globalData?.userId,
            fileName: fileContent.name,
            fileSize: fileContent.size,
            mimeType: fileContent.type,
          };

          // Upload file to regular blob storage
          const fileResponse = await axios.post("/api/v1/file", fileInfo);
          uploadedFileId = fileResponse.data.fileId; // Store the fileId
          const formData = new FormData();
          formData.append("file", fileContent, uploadedFileId);
          await axios.post("/api/v1/azureblobstorage", formData);

          // If the invoice has a customerInvoiceId, link the file
          const customerInvoiceId = viewCustomerInvoiceDialog?.params?.customerInvoiceId;
          if (customerInvoiceId) {
            const fileIdInfo = { fileId: uploadedFileId };
            await axios.put(`/api/v1/customerInvoices/updateFileId/${customerInvoiceId}`, fileIdInfo);
          }
        }

        setIsNewFile(false);
        setIsUpdatedFile(false);
        setIsRemovedFile(false);
      } catch (error) {
        console.error("Error uploading file:", error);
        setErrors({ file: "Failed to upload file" });
        return;
      }
    }

    const updatedInvoice = {
      id: viewCustomerInvoiceDialog?.params?.id,
      invoiceId: viewCustomerInvoiceDialog?.params?.invoiceId,
      invoiceNumber: values.invoiceNumber.trim(),
      customerNumber: values.customerNumber,
      invoiceDate: values.invoiceDate,
      supplierId: values.supplierId,
      countryCode: values.countryCode,
      countryCodeCustomer: props.countryArr.find((country) => country.countryId === newCustomer.countryId).countryCode,
      countryCodeSupplier: props.countryArr.find((country) => country.countryId === values.countryCode).countryCode,
      taxIdCustomer: props.entitiesArr.find((entity) => entity.entityNumber === values.customerNumber).vat,
      taxIdSupplier: mergedSupplierData.find((supplier) => supplier.supplierId === values.supplierId)?.supplierVATnr,
      currencyId: values.currencyId,
      totalExcludingTax: parseFloat(values.invoiceGrossAmount) || 0,
      totalTax: parseFloat(values.invoiceVATAmountLocalCurrency) || 0,
      claimProductSubCodeId: values.claimProductSubCodeId,
      claimProductTypeCode: values.claimProductTypeCode,
      productId: matchedProduct.productTypeId,
      invoiceStateId: values.invoiceStateIdStatic,
      // Preserve existing fileId if no new file was uploaded, otherwise use the new uploadedFileId
      // Only include fileId if it has a valid value (not null, undefined, or 0)
      ...((uploadedFileId || viewCustomerInvoiceDialog?.params?.fileId) && {
        fileId: uploadedFileId || viewCustomerInvoiceDialog?.params?.fileId,
      }),
    };

    console.log("updatedInvoice1", updatedInvoice);

    props.updateInvoice(updatedInvoice);

    setViewCustomerInvoiceDialog({
      ...viewCustomerInvoiceDialog,
      isOpen: false,
    });
  };

  const filteredSuppliers = useMemo(() => {
    return mergedSupplierData
      .filter((supplier) => supplier.countryCode === currentCountryCode)
      .sort((a, b) => (a.supplierName || "").localeCompare(b.supplierName || "", undefined, { sensitivity: "base" }));
  }, [mergedSupplierData, currentCountryCode]);

  const selectedCountryName = useMemo(() => {
    if (!currentCountryCode) {
      return "";
    }

    const selectedCountry = props.countryArr?.find((country) => country.countryId === currentCountryCode);
    return selectedCountry?.countryName || "";
  }, [currentCountryCode, props.countryArr]);

  const buildReviewOcrFieldDefinitions = useCallback(
    (values) => buildReviewOcrInvoiceFieldDefinitions(values, props.entitiesArr, showExtraFields),
    [props.entitiesArr, showExtraFields]
  );

  return (
    <>
    <Formik
      key={activeTab === 3 ? viewCustomerInvoiceDialog?.params?.id : undefined}
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => {
        formHelpersRef.current = { setFieldValue, handleChange, values };
        const invoiceDateWarning = getInvoiceDateHelperText(values.invoiceDate);
        const trimmedCustomerNumber = String(values.customerNumber || "").trim();
        const selectedCustomerEntity = props.entitiesArr?.find(
          (entity) => entity.entityNumber === trimmedCustomerNumber
        );
        const selectedCustomerName = selectedCustomerEntity?.name || "";
        const customerNumberError =
          trimmedCustomerNumber && !selectedCustomerEntity ? "Customer number does not exist." : "";
        const detailCountryVatPercentage = (() => {
          if (!values.countryCode) {
            return null;
          }

          const selectedCountry = props.countryArr?.find((country) => country.countryId === values.countryCode);
          return parseNumberOrNull(selectedCountry?.vatPercentage);
        })();
        const selectedCurrency = values.currencyId
          ? props.currencyArr?.find((currency) => String(currency.currencyId) === String(values.currencyId)) || null
          : null;
        const selectedCurrencyFractionDigits = (() => {
          const currencyIsoCode = selectedCurrency?.isoCode;
          if (!currencyIsoCode) {
            return 2;
          }

          try {
            return (
              new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: currencyIsoCode,
              }).resolvedOptions().maximumFractionDigits ?? 2
            );
          } catch (error) {
            return 2;
          }
        })();
        const allowedVatDifference = (() => {
          const marginInSmallestUnit = parseNumberOrNull(selectedCurrency?.margin);
          if (marginInSmallestUnit === null) {
            return 0;
          }

          return marginInSmallestUnit / 10 ** selectedCurrencyFractionDigits;
        })();
        const calculatedCountryVatAmount = (() => {
          const netAmount = parseNumberOrNull(values.invoiceGrossAmount);
          if (netAmount === null || detailCountryVatPercentage === null) {
            return null;
          }

          return (netAmount * detailCountryVatPercentage) / 100;
        })();
        const isVatAmountMismatch = (() => {
          const enteredVatAmount = parseNumberOrNull(values.invoiceVATAmountLocalCurrency);
          if (enteredVatAmount === null || calculatedCountryVatAmount === null) {
            return false;
          }

          const vatDifference = Math.abs(enteredVatAmount - calculatedCountryVatAmount);
          return vatDifference - allowedVatDifference > 0.000001;
        })();
        const currencyVatHelperText = (() => {
          if (!values.countryCode) {
            return "Select country first";
          }

          if (detailCountryVatPercentage === null) {
            return "No VAT % configured for this country";
          }

          const netAmount = parseNumberOrNull(values.invoiceGrossAmount);
          if (netAmount === null) {
            return `VAT %: ${detailCountryVatPercentage}`;
          }

          return `VAT %: ${detailCountryVatPercentage} | Calculated VAT: ${formatToTwoDecimals(calculatedCountryVatAmount)}`;
        })();

        return (
        <>
        <FocusFirstEmptyInvoiceFieldEffect
          containerRef={invoiceFormPanelRef}
          selectionKey={viewCustomerInvoiceDialog?.params?.id}
          buildFieldDefinitions={buildReviewOcrFieldDefinitions}
        />
        <Card mb={6} sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
          <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", padding: 0, paddingTop: 2 }}>
            <Snackbar
              open={openAlert}
              autoHideDuration={5000}
              onClose={handleCloseAlert}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert onClose={handleCloseAlert} severity={alertSeverity} sx={{ width: "100%" }}>
                {alertMessage}
              </Alert>
            </Snackbar>

            <form onSubmit={handleSubmit} style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
              <Grid container spacing={6} sx={{ flexGrow: 1 }}>
                {/* Left fields */}
                <Grid item>
                  <Grid ref={invoiceFormPanelRef} container spacing={2} direction="column">
                    <Grid item xs={12}>
                      <CustomerSearchSelect
                        customers={props.entitiesArr || []}
                        value={values.customerNumber}
                        onSelect={(customer) => {
                          setFieldValue("customerNumber", customer.customerNumber);
                          scheduleInvoiceNumberCheck();
                        }}
                        onClear={() => {
                          setFieldValue("customerNumber", "");
                          setInvoiceNumberInfo("");
                        }}
                        label="Customer Number"
                        helperText={
                          (touched.customerNumber && errors.customerNumber) ||
                          customerNumberError ||
                          selectedCustomerName ||
                          " "
                        }
                        error={Boolean((touched.customerNumber && errors.customerNumber) || customerNumberError)}
                        sx={{ width: FORM_FIELD_WIDTH, minWidth: 150, maxWidth: FORM_FIELD_WIDTH }}
                        displayMode="number"
                        clearOnType
                        invoiceFieldKey="customerNumber"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="invoiceNumber"
                        label="Invoice Number"
                        value={values.invoiceNumber}
                        error={Boolean(touched.invoiceNumber && errors.invoiceNumber)}
                        helperText={(touched.invoiceNumber && errors.invoiceNumber) || invoiceNumberInfo || " "}
                        FormHelperTextProps={{
                          sx: invoiceNumberInfo ? { color: "warning.main" } : undefined,
                        }}
                        onBlur={(event) => {
                          handleBlur(event);
                          handleInvoiceNumberBlur();
                        }}
                        onChange={(event) => {
                          handleChange(event);
                          if (invoiceNumberInfo) {
                            setInvoiceNumberInfo("");
                          }
                          scheduleInvoiceNumberCheck();
                        }}
                        onPaste={(event) => {
                          event.preventDefault();
                          const pasted = event.clipboardData.getData("text").trim();
                          setFieldValue("invoiceNumber", pasted);
                          if (invoiceNumberInfo) {
                            setInvoiceNumberInfo("");
                          }
                          scheduleInvoiceNumberCheck();
                        }}
                        type="text"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: !!values.invoiceNumber,
                        }}
                        my={2}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="invoiceDate"
                        name="invoiceDate"
                        label="Invoice date"
                        type="date"
                        value={values.invoiceDate}
                        onChange={(e) => {
                          handleChange(e);
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={Boolean(touched.invoiceDate && errors.invoiceDate)}
                        helperText={
                          touched.invoiceDate && errors.invoiceDate ? errors.invoiceDate : invoiceDateWarning || " "
                        }
                        FormHelperTextProps={{
                          sx: !errors.invoiceDate && invoiceDateWarning ? { color: "warning.main" } : undefined,
                        }}
                        variant="outlined"
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectWrapper
                        name="invoiceStateIdStatic"
                        label="Invoice State"
                        value={values.invoiceStateIdStatic}
                        onChange={(e) => {
                          handleChange(e);
                        }}
                        options={props.invoiceStateArr.map((item) => ({
                          key: item.invoiceStateId,
                          value: item.invoiceStateDescription,
                        }))}
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ "& .MuiFormControl-root": { marginBottom: 0.5 } }}>
                      <SelectWrapper
                        name="countryCode"
                        label="Country"
                        value={values.countryCode}
                        onChange={(e) => {
                          const newCountryId = e.target.value;
                          handleChange(e);
                          setCurrentCountryCode(newCountryId);

                          // Automatically set currency based on selected country (only for Hard Error Invoices tab)
                          if (activeTab === 3 && newCountryId && props.countryArr) {
                            const selectedCountry = props.countryArr.find((country) => country.countryId === newCountryId);
                            if (selectedCountry && selectedCountry.currency) {
                              setFieldValue("currencyId", selectedCountry.currency);
                            }
                          }

                          setFieldValue("claimProductTypeCode", "");
                          setFieldValue("claimProductSubCodeId", "");
                          setCurrentProductCode("");
                          setCurrentProductSubCode("");
                        }}
                        options={props.countryArr.map((country) => ({
                          key: country.countryId,
                          value: country.countryCode,
                        }))}
                        helperText={selectedCountryName || " "}
                        FormHelperTextProps={{ sx: { mt: 0.25, mb: 0.75, lineHeight: 1.2 } }}
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: -0.375 }}>
                      <Box sx={{ position: "relative", width: FORM_FIELD_WIDTH }}>
                        <SelectWrapper
                          name="supplierId"
                          label="Supplier"
                          value={values.supplierId}
                          options={filteredSuppliers.map((supplier) => ({
                            key: supplier.supplierId,
                            value: supplier.supplierName,
                          }))}
                          onChange={(event) => handleSupplierChange(event, setFieldValue, handleChange, currentCountryCode)}
                          my={0.5}
                          style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                        />
                        <Tooltip title="Add supplier">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Add supplier"
                              tabIndex={-1}
                              onClick={() => setIsCreateSupplierDialogOpen(true)}
                              disabled={!currentCountryCode}
                              disableRipple
                              disableFocusRipple
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 0,
                                transform: "translateY(-100%)",
                                zIndex: 2,
                                p: 0.25,
                                borderRadius: 0,
                                bgcolor: "transparent",
                                "&:hover": {
                                  bgcolor: "transparent",
                                },
                              }}
                            >
                              <AddIcon fontSize="small" sx={{ color: "primary.main" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <SelectWrapper
                        name="claimProductTypeCode"
                        label="Product Type"
                        value={values.claimProductTypeCode}
                        SelectProps={{
                          MenuProps: {
                            TransitionProps: {
                              timeout: 0,
                            },
                          },
                        }}
                        onChange={(e) => {
                          const newProductCode = e.target.value;

                          // Update form values
                          handleChange(e);
                          setCurrentProductCode(newProductCode);
                          setFieldValue("claimProductSubCodeId", "");
                          setCurrentProductSubCode("");

                          // Immediately remove Product Type menu from DOM to prevent it from catching clicks
                          // Use both synchronous and async approaches for maximum reliability
                          const productTypeInput = document.querySelector('[name="claimProductTypeCode"]');
                          if (productTypeInput) {
                            // Blur immediately
                            const input = productTypeInput.querySelector("input");
                            if (input) input.blur();

                            // Also remove menu immediately (don't wait for requestAnimationFrame)
                            const allMenus = document.querySelectorAll(".MuiMenu-root");
                            allMenus.forEach((menu) => {
                              if (menu.offsetParent !== null) {
                                const menuRect = menu.getBoundingClientRect();
                                const inputRect = productTypeInput.getBoundingClientRect();
                                if (Math.abs(menuRect.left - inputRect.left) < 50 && Math.abs(menuRect.top - inputRect.bottom) < 300) {
                                  menu.remove();
                                }
                              }
                            });
                          }
                        }}
                        options={groupedProductCodes}
                        error={Boolean(touched.claimProductTypeCode && errors.claimProductTypeCode)}
                        helperText={touched.claimProductTypeCode && errors.claimProductTypeCode ? errors.claimProductTypeCode : ""}
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectWrapper
                        name="claimProductSubCodeId"
                        label="Product Sub Code"
                        value={values.claimProductSubCodeId}
                        options={groupedProductSubCodes}
                        onChange={(e) => {
                          const newSubCode = e.target.value;
                          handleChange(e);
                          setCurrentProductSubCode(newSubCode);
                        }}
                        error={Boolean(touched.claimProductSubCodeId && errors.claimProductSubCodeId)}
                        helperText={touched.claimProductSubCodeId && errors.claimProductSubCodeId ? errors.claimProductSubCodeId : ""}
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>
                    {showExtraFields && (
                      <>
                        <Grid item xs={12}>
                          <SelectWrapper
                            name="languageCode"
                            label="FreeText Language"
                            value={values.languageCode}
                            options={customSelectableLanguages}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            my={1}
                            style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="freeText"
                            label="FreeText"
                            value={values.freeText}
                            error={Boolean(touched.freeText && errors.freeText)}
                            fullWidth
                            helperText={touched.freeText && errors.freeText ? errors.freeText : ""}
                            onBlur={handleBlur}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            type="text"
                            variant="outlined"
                            my={1}
                            style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        name="invoiceGrossAmount"
                        label="Net Amount"
                        value={values.invoiceGrossAmount || ""}
                        error={Boolean(touched.invoiceGrossAmount && errors.invoiceGrossAmount)}
                        fullWidth
                        helperText={touched.invoiceGrossAmount && errors.invoiceGrossAmount ? errors.invoiceGrossAmount : ""}
                        onBlur={(e) => {
                          handleBlur(e);
                          const formattedValue = formatToTwoDecimals(e.target.value);
                          setFieldValue("invoiceGrossAmount", formattedValue);
                        }}
                        onChange={handleChange}
                        type="text"
                        inputMode="decimal"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: !!values.invoiceGrossAmount,
                        }}
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        name="invoiceVATAmountLocalCurrency"
                        label="VAT Amount"
                        value={values.invoiceVATAmountLocalCurrency || ""}
                        error={Boolean(touched.invoiceVATAmountLocalCurrency && errors.invoiceVATAmountLocalCurrency)}
                        fullWidth
                        helperText={
                          touched.invoiceVATAmountLocalCurrency && errors.invoiceVATAmountLocalCurrency
                            ? errors.invoiceVATAmountLocalCurrency
                            : ""
                        }
                        onBlur={(e) => {
                          handleBlur(e);
                          const formattedValue = formatToTwoDecimals(e.target.value);
                          setFieldValue("invoiceVATAmountLocalCurrency", formattedValue);
                        }}
                        onChange={handleChange}
                        type="text"
                        inputMode="decimal"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: !!values.invoiceVATAmountLocalCurrency,
                        }}
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <SelectWrapper
                        name="currencyId"
                        label="Currency"
                        value={values.currencyId}
                        onChange={handleChange}
                        options={props.currencyArr.map((currency) => ({
                          key: currency.currencyId,
                          value: currency.isoCode,
                        }))}
                        helperText={currencyVatHelperText || " "}
                        FormHelperTextProps={{
                          sx: isVatAmountMismatch
                            ? {
                                color: "error.main",
                              }
                            : undefined,
                        }}
                        my={1}
                        style={{ width: FORM_FIELD_WIDTH, margin: 0 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* PDF-pane (right) */}
                <Grid item sx={{ flexGrow: 1 }}>
                  <Box style={{ width: "100%", height: "100%", overflow: "auto", display: "flex", flexDirection: "column" }}>
                    {fileUrl === "" ? (
                      <Typography
                        sx={{
                          fontSize: "21px",
                          mt: 2,
                          pt: "20px",
                          pb: "20px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          border: 1,
                          borderColor: "lightgray",
                          width: "100%",
                          height: "100%",
                          boxSizing: "border-box",
                        }}
                      >
                        <CircularProgress size={30} sx={{ mr: 2 }} />
                        Loading file, please wait!
                      </Typography>
                    ) : fileUrl === "NoFile" || fileUrl === null ? (
                      activeTab === 3 ? (
                        <Typography
                          sx={{
                            fontSize: "21px",
                            mt: 2,
                            pt: "20px",
                            pb: "20px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: 1,
                            borderColor: "lightgray",
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                          }}
                        >
                          No files are currently attached to this invoice.
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            mt: 2,
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                          }}
                        >
                          <StyledDropzone />
                        </Box>
                      )
                    ) : (
                      <Box style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", flexGrow: 1 }}>
                        <Dialog open={showConfirmationDialog} onClose={handleConfirmationDialogClose}>
                          <DialogTitle>Confirmation</DialogTitle>
                          <DialogContent>
                            <DialogContentText>Are you sure you want to delete the invoice copy?</DialogContentText>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={handleConfirmationDialogClose} color="primary">
                              Cancel
                            </Button>
                          </DialogActions>
                        </Dialog>
                        <object
                          width="100%"
                          height="100%"
                          data={fileUrl}
                          type="application/pdf"
                          style={{
                            marginTop: "8px",
                            flex: 1,
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                          }}
                        >
                          <iframe title="file" src={fileUrl + "&embedded=true"} style={{ width: "100%", height: "100vh" }} />
                        </object>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Update button */}
              <Box display="flex" justifyContent="flex-start" mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!canWriteInvoices || isSubmitting}
                  aria-label="Update invoice"
                  sx={{ mr: 4 }}
                >
                  Update Invoice
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
        <CustomerClipboardOverlay
          open={Boolean(viewCustomerInvoiceDialog?.isOpen && viewCustomerInvoiceDialog?.params?.id)}
          entityId={selectedCustomerEntity?.entityId || null}
          countryData={props.countryArr || []}
          productData={productTypeData || []}
          supplierData={mergedSupplierData}
          getFormValues={getClipboardFormValues}
          onApply={handleClipboardApply}
          onNotify={handleClipboardNotify}
          selectionRequiredMessage="Select an invoice before using clipboard."
        />
        <SupplierProductNotesOverlay
          open={Boolean(viewCustomerInvoiceDialog?.isOpen && viewCustomerInvoiceDialog?.params?.id)}
          supplierId={values.supplierId}
          claimProductTypeCode={values.claimProductTypeCode}
          claimProductSubCode={values.claimProductSubCodeId}
        />
        </>
        );
      }}
    </Formik>
    {isCreateSupplierDialogOpen && (
      <Box
        role="button"
        tabIndex={0}
        aria-label="Close add supplier popup overlay"
        onClick={handleCloseCreateSupplierDialog}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " " || event.key === "Escape") {
            event.preventDefault();
            handleCloseCreateSupplierDialog();
          }
        }}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          backgroundColor: "rgba(0, 0, 0, 0.12)",
          cursor: "pointer",
        }}
      />
    )}
    <CreateSupplierDialog
      open={isCreateSupplierDialogOpen}
      onClose={handleCloseCreateSupplierDialog}
      onCreated={handleSupplierCreated}
      defaultCountryCode={currentCountryCode}
      suppliers={mergedSupplierData}
    />
  </>
  );
}
